/**
 * Calendar dates are plain `YYYY-MM-DD` strings — a timesheet day is a
 * calendar day, not an instant, so no timezone is ever attached to one.
 *
 * The one place timezones matter is deciding what "today" means. The browser
 * knows the person's local day; the Worker only knows UTC. See `isFutureDate`.
 */

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * A well-formed date that is also a real day.
 *
 * Date.parse is not enough: it rolls 2026-02-29 over to 1 March and returns a
 * valid timestamp, so the components have to survive a round trip.
 */
export function isRealDate(value: string): boolean {
  if (!DATE_PATTERN.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  )
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * The calendar date in the *viewer's own* timezone.
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC date, which is a day
 * behind for anyone east of Greenwich during their morning — in Perth
 * (UTC+8) everything logged before 8am would file under yesterday.
 */
export function todayLocal(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The calendar date in UTC — all the Worker can know on its own. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

/** Shift a date string by whole days. */
export function addDays(date: string, delta: number): string {
  const [year, month, day] = date.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, month - 1, day + delta))
  return shifted.toISOString().slice(0, 10)
}

/** Whole days from `from` to `to`, negative if `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)
  return Math.round(ms / 86_400_000)
}

/**
 * Whether a date is in the future, from the Worker's UTC-only point of view.
 *
 * A timesheet should not accept future dates, but the Worker cannot know the
 * person's local day. The largest real offset is UTC+14, so somebody's "today"
 * can be one day ahead of UTC's. Allowing that one day of grace means no real
 * timezone is ever wrongly refused its own today; the cost is that a person on
 * UTC-5 could log one day ahead if they tried.
 */
export function isFutureDate(date: string, now: Date = new Date()): boolean {
  return daysBetween(todayUtc(now), date) > 1
}

/** Monday first, matching how the week is shown in the UI. */
export const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/**
 * The Monday of the week containing `date`.
 *
 * Weeks run Monday to Sunday here, so Sunday belongs to the week that started
 * six days earlier, not the one beginning that evening.
 */
export function startOfWeek(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  // getUTCDay is 0 for Sunday; shift so Monday is 0 and Sunday is 6.
  const offset = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7
  return addDays(date, -offset)
}

/** The seven dates of the week containing `date`, Monday first. */
export function weekOf(date: string): string[] {
  const monday = startOfWeek(date)
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Today", "Yesterday", or "Mon 14 Sep 2026" for anything further back. */
export function formatDateLabel(date: string, today: string = todayLocal()): string {
  const delta = daysBetween(today, date)
  if (delta === 0) return 'Today'
  if (delta === -1) return 'Yesterday'
  if (delta === 1) return 'Tomorrow'

  const [year, month, day] = date.split('-').map(Number)
  const d = new Date(Date.UTC(year, month - 1, day))
  return `${WEEKDAYS[d.getUTCDay()]} ${day} ${MONTHS[month - 1]} ${year}`
}
